import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import AccountSelect, { decodeAccountSelection, encodeLedgerSelection, encodeVendorSelection, useAccountOptions } from "./AccountSelect";
import { blankGridRow, formatCurrency } from "./LineGrid";
import PartySearchInput from "./PartySearchInput";

/**
 * JOURNAL VOUCHER — SIMPLE TALLY-STYLE CORRECTION.
 *
 * A dedicated, compact Dr/Cr row editor used ONLY by the Journal Voucher
 * screens (JournalVoucherPage.jsx for create, VoucherEditPage.jsx's JOURNAL
 * branch for edit) - deliberately a SEPARATE component from the shared
 * LineGrid.jsx (which Credit Note, Debit Note, Purchase and the generic
 * Draft editor still use, completely untouched) so this redesign can never
 * leak into those other voucher types.
 *
 * Each row is still the exact same {selection, debit, credit, partyName,
 * referenceNo, narration} shape LineGrid/gridRowsToLines already use - only
 * the on-screen presentation changes (one Dr/Cr toggle + one Amount field
 * instead of separate always-visible Debit/Credit/Party/Reference/Line-
 * narration columns), so gridRowsToLines(rows) still produces the exact same
 * voucher line payload the engine has always accepted. No accounting engine
 * change of any kind.
 *
 * Ledger choices always use AccountSelect's existing "journalAdjustment"
 * context (Income/Expense/Asset/Liability/Fund/Equity/Other only) with
 * hideCash/hideBankAccounts forced true - the old opt-in "Include Bank/Cash
 * ledgers" checkbox is gone entirely per this correction's item 5, not just
 * hidden by default.
 *
 * JOURNAL LEDGER CONTROL correction: raw ledgerGroup "vendor" ledgers stay
 * excluded (that group isn't in the journalAdjustment allow-list), but
 * Vendor ENTITIES are now offered (hideVendors=false) - each Vendor already
 * has its own dedicated 1:1 ledger (see engine_create_vendor on the
 * backend), so picking the Vendor itself both posts to the right ledger
 * AND is its own unambiguous subledger; no separate party field is needed
 * for a vendor line. Member Receivables/Member Advances are different: they
 * are ONE ledger shared by every member (see billing-settings
 * memberReceivableLedgerId/memberAdvanceLedgerId), so selecting either one
 * here reveals a conditional "Member / Flat" field (reusing the same
 * PartySearchInput/Owner Directory search every other member-facing posting
 * already uses) which is required before saving - enforced again
 * server-side by _assert_journal_member_control_ledger_party regardless of
 * what the browser sends.
 */
export function blankJournalRow() {
  return { ...blankGridRow(), side: "Dr" };
}

/** Rehydrate a saved voucher line back into the row shape this editor uses,
 * for the JOURNAL edit branch in VoucherEditPage.jsx. JOURNAL LEDGER CONTROL
 * correction: a vendorId line is now a normal, fully-supported case (the
 * Vendor's own dedicated ledger, picked directly via the Vendors bucket -
 * see the AccountSelect usage below), not just a legacy leftover, so it is
 * reconstructed exactly like a plain ledger selection. Only a bankAccountId
 * line still has no representation here (Bank/Cash stays excluded from
 * Journal per item 4) - VoucherEditPage.jsx's isSimpleJournal guard falls
 * back to the generic grid editor for that case only. */
export function journalLineToRow(line) {
  const debit = Number(line.debit) || 0;
  const credit = Number(line.credit) || 0;
  let selection = "";
  if (line.vendorId) selection = encodeVendorSelection(line.vendorId);
  else if (line.ledgerId) selection = encodeLedgerSelection(line.ledgerId);
  return {
    key: line.id || Math.random().toString(36).slice(2),
    selection,
    side: credit > 0 ? "Cr" : "Dr",
    debit: debit > 0 ? debit : "",
    credit: credit > 0 ? credit : "",
    partyName: line.partyName || "", referenceNo: line.referenceNo || "", narration: line.narration || "",
  };
}

/** Small "Bal: ₹12,345 Cr" hint shown under a selected ledger - read-only,
 * fetched from the existing /engine/ledgers/:id/balance endpoint (already
 * used by LedgerViewPage.jsx) so this is purely a read, never a new balance
 * computation of its own. */
function LedgerBalanceHint({ ledgerId }) {
  const [balance, setBalance] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!ledgerId) { setBalance(null); return undefined; }
    api.get(`/society/accounting/engine/ledgers/${ledgerId}/balance`).then((res) => {
      if (!cancelled) setBalance(res.data);
    }).catch(() => { if (!cancelled) setBalance(null); });
    return () => { cancelled = true; };
  }, [ledgerId]);
  if (!balance) return null;
  return (
    <div className="mt-0.5 text-[11px] text-[#6B7280]">
      Bal: {formatCurrency(balance.balance)} {balance.side === "credit" ? "Cr" : "Dr"}
    </div>
  );
}

/** Which ledger ids are the shared Member control ledgers - fetched once
 * from the same /billing-settings the Billing Ledger Mapping page reads/
 * writes, so this never drifts from whatever ledger an admin has actually
 * mapped there. */
function useMemberControlLedgerIds() {
  const [ids, setIds] = useState(new Set());
  useEffect(() => {
    let cancelled = false;
    api.get("/society/accounting/billing-settings").then((res) => {
      if (cancelled) return;
      const { memberReceivableLedgerId, memberAdvanceLedgerId } = res.data || {};
      setIds(new Set([memberReceivableLedgerId, memberAdvanceLedgerId].filter(Boolean)));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return ids;
}

export default function JournalRows({ rows, onChange, minRows = 2 }) {
  const options = useAccountOptions();
  const memberControlLedgerIds = useMemberControlLedgerIds();

  const updateRow = (key, patch) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => onChange([...rows, blankJournalRow()]);
  const removeRow = (key) => {
    if (rows.length <= minRows) return;
    onChange(rows.filter((r) => r.key !== key));
  };

  const setSide = (row, side) => {
    const amount = row.debit || row.credit || "";
    updateRow(row.key, { side, debit: side === "Dr" ? amount : "", credit: side === "Cr" ? amount : "" });
  };
  const setAmount = (row, amount) => {
    updateRow(row.key, { debit: row.side === "Dr" ? amount : "", credit: row.side === "Cr" ? amount : "" });
  };

  const totalDebit = rows.reduce((sum, r) => sum + (Number(r.debit) || 0), 0);
  const totalCredit = rows.reduce((sum, r) => sum + (Number(r.credit) || 0), 0);
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB] text-left text-xs uppercase tracking-[0.1em] text-[#6B7280]">
              <th className="px-2 py-2 w-20">Dr / Cr</th>
              <th className="px-2 py-2">Particulars / Ledger</th>
              <th className="px-2 py-2 text-right w-36">Amount</th>
              <th className="px-2 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const decoded = decodeAccountSelection(row.selection);
              const requiresMemberParty = !!decoded?.ledgerId && memberControlLedgerIds.has(decoded.ledgerId);
              return (
                <tr key={row.key} className="border-b border-[#F3F4F6]">
                  <td className="px-2 py-1.5 align-top">
                    <Select value={row.side} onValueChange={(v) => setSide(row, v)}>
                      <SelectTrigger className="w-16"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Dr">Dr</SelectItem>
                        <SelectItem value="Cr">Cr</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-2 py-1.5 min-w-[240px] align-top">
                    <AccountSelect
                      value={row.selection}
                      onChange={(v) => updateRow(row.key, { selection: v })}
                      options={options}
                      hideCash hideBankAccounts
                      context="journalAdjustment"
                      placeholder="Select ledger or vendor"
                      searchPlaceholder="Search ledger (Assets, Liabilities, Income, Expenses, Funds...) or Vendor"
                    />
                    {decoded?.ledgerId && <LedgerBalanceHint ledgerId={decoded.ledgerId} />}
                    {requiresMemberParty && (
                      <div className="mt-1.5">
                        <PartySearchInput
                          value={row.partyName}
                          onChange={(v) => updateRow(row.key, { partyName: v })}
                          placeholder="Member / Flat (required for this ledger)"
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 w-36 align-top">
                    <Input
                      type="number" min="0" step="0.01" className="text-right"
                      value={row.debit || row.credit || ""}
                      onChange={(e) => setAmount(row, e.target.value)}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right align-top">
                    <Button type="button" variant="outline" size="sm" disabled={rows.length <= minRows} onClick={() => removeRow(row.key)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Button type="button" variant="outline" size="sm" onClick={addRow}><Plus className="mr-2 h-3.5 w-3.5" /> Add Row</Button>

      <div className="flex flex-wrap justify-end gap-6 rounded-sm border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm">
        <div>Total Dr: <span className="font-semibold text-[#111827]">{formatCurrency(totalDebit)}</span></div>
        <div>Total Cr: <span className="font-semibold text-[#111827]">{formatCurrency(totalCredit)}</span></div>
        <div>Difference: <span className={`font-semibold ${difference === 0 ? "text-emerald-600" : "text-red-600"}`}>{formatCurrency(Math.abs(difference))}</span></div>
      </div>
    </div>
  );
}
